// src/components/Vocabulary/Vocabulary.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Vocabulary.css';

const Vocabulary: React.FC = () => {
  const navigate = useNavigate();
  
  // Helper functions to get activity colors - matching Story Creator Studio theme
  const getActivityColor = (className: string) => {
    const colorMap: { [key: string]: string } = {
      'flashcards': '#FF6B35',
      'memory-game': '#3498DB', 
      'crossword': '#9B59B6',
      'word-factory': '#2ECC71',
      'dragon-racing': '#FF6B35' // Using the same orange as flashcards for consistency
    };
    return colorMap[className] || '#FF6B35';
  };

  const getActivityGradient = (className: string) => {
    const gradientMap: { [key: string]: string } = {
      'flashcards': 'linear-gradient(135deg, #FF6B35, #E55A2B)',
      'memory-game': 'linear-gradient(135deg, #3498DB, #2980B9)',
      'crossword': 'linear-gradient(135deg, #9B59B6, #8E44AD)',
      'word-factory': 'linear-gradient(135deg, #2ECC71, #27AE60)',
      'dragon-racing': 'linear-gradient(135deg, #FF6B35, #E74C3C)' // Orange to red gradient for dragons
    };
    return gradientMap[className] || 'linear-gradient(135deg, #FF6B35, #E55A2B)';
  };
  
  const activities = [
    {
      id: 'flashcards',
      title: 'Vocabulary Cards',
      description: 'Learn and review new words with interactive flashcards. Expand your vocabulary across different topics and difficulty levels.',
      icon: '🔤',
      onClick: () => navigate('/vocabulary-flashcards'),
      className: 'flashcards',
      xp: '+12 XP per deck',
      status: '4 decks available'
    },
    {
      id: 'memory',
      title: 'Memory Match',
      description: 'Test your memory skills by matching words with their definitions. A fun way to reinforce vocabulary knowledge.',
      icon: '🧠',
      onClick: () => navigate('/vocabulary-memory'),
      className: 'memory-game',
      xp: '+18 XP per game',
      status: '6 levels unlocked'
    },
    {
      id: 'dragon-racing',
      title: 'Dragon Racing',
      description: 'Race your magical dragon across the sky by typing words quickly and accurately. Compete against fire, storm, and ice dragons!',
      icon: '🐲',
      onClick: () => navigate('/dragon-racing'),
      className: 'dragon-racing',
      xp: '+25 XP per race',
      status: 'Epic races available'
    },
    {
      id: 'crossword',
      title: 'Crossword Puzzles',
      description: 'Challenge yourself with crossword puzzles designed to build your vocabulary. Use context clues to complete each puzzle.',
      icon: '📝',
      onClick: () => navigate('/crosswords'),
      className: 'crossword',
      xp: '+25 XP complete',
      status: 'Daily puzzles'
    },
    {
      id: 'word-factory',
      title: 'Word Factory',
      description: 'Build amazing new words using prefixes, roots, and suffixes! Discover word families and create your own vocabulary.',
      icon: '🏭',
      onClick: () => navigate('/word-factory'),
      className: 'word-factory',
      xp: '+20 XP complete',
      status: 'Word building lab'
    }
  ];

  return (
    <div className="vocabulary-container">
      {/* Header with Reward Tracking - matching Story Creator Studio */}
      <div className="vocabulary-header">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <h2>Vocabulary Power Center</h2>
            <div className="vocabulary-level-info">
              <div className="vocabulary-level-badge">
                <span>📚</span>
                Level 3 Word Master
              </div>
              <div className="vocabulary-xp-info">
                150/250 XP to Level 4
              </div>
            </div>
          </div>
          
          <button style={{
            background: 'linear-gradient(45deg, #FF6B35, #F39C12)',
            color: 'white',
            border: 'none',
            padding: '16px 28px',
            borderRadius: '20px',
            fontSize: '1.1rem',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
          }}
          >
            🏆 Word Rewards
          </button>
        </div>

        <div className="vocabulary-xp-bar">
          <div className="vocabulary-xp-progress"></div>
        </div>

        <div className="vocabulary-stats-grid">
          <div className="vocabulary-stat-card words-learned">
            <div className="vocabulary-stat-number">234</div>
            <div className="vocabulary-stat-label">Words Learned</div>
          </div>
          
          <div className="vocabulary-stat-card quiz-score">
            <div className="vocabulary-stat-number">88%</div>
            <div className="vocabulary-stat-label">Quiz Average</div>
          </div>
          
          <div className="vocabulary-stat-card streak fire">
            <div className="vocabulary-stat-number">12</div>
            <div className="vocabulary-stat-label">Day Streak</div>
          </div>
        </div>
      </div>

      {/* Activity Cards - matching Story Creator Studio structure exactly */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: '32px',
        marginBottom: '40px'
      }}>
        {activities.map((activity, index) => (
          <div key={activity.id} 
            onClick={activity.onClick}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              border: `4px solid ${getActivityColor(activity.className)}`,
              boxShadow: '0 15px 50px rgba(0,0,0,0.15)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 25px 60px rgba(0,0,0,0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 15px 50px rgba(0,0,0,0.15)';
            }}
          >
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '120px',
              height: '120px',
              background: getActivityGradient(activity.className),
              borderRadius: '50%',
              opacity: 0.1
            }} />
            
            {/* XP Reward Badge */}
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: getActivityGradient(activity.className),
              color: 'white',
              borderRadius: '16px',
              padding: '8px 12px',
              fontSize: '0.9rem',
              fontWeight: '700',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}>
              {activity.xp}
            </div>
            
            <div style={{
              width: '80px',
              height: '80px',
              background: getActivityGradient(activity.className),
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              color: 'white',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
              fontSize: '40px',
              transition: 'all 0.3s ease'
            }}>
              {activity.icon}
            </div>
            
            <h3 style={{
              fontSize: '1.8rem',
              fontWeight: '800',
              color: '#333',
              margin: '0 0 16px 0'
            }}>
              {activity.title}
            </h3>
            
            <p style={{
              color: '#666',
              fontSize: '1.1rem',
              lineHeight: '1.6',
              margin: '0 0 24px 0'
            }}>
              {activity.description}
            </p>
            
            <div style={{
              background: `${getActivityColor(activity.className)}15`,
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '1rem',
              fontWeight: '600',
              color: getActivityColor(activity.className),
              display: 'inline-block',
              border: `2px solid ${getActivityColor(activity.className)}30`
            }}>
              {activity.status}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Vocabulary;